/** THE GUEST FLOW — one thing per screen.
 *
 *  The pieces the fan screen uses when a guest leaves the main page: the first
 *  language choice, the Back bar every screen wears, the two teams head to
 *  head, the "lock it in" confirmation, the roster A–Z and the goodbye
 *  summary. Every one of them is DUMB: no fetching, no rules — the fan view
 *  owns the saving and hands the answer back down.
 */
import { useMemo, useState } from 'react';
import { ArrowLeft, Check, Loader2, Search, Star, Trophy } from 'lucide-react';
import { colourFor } from './GoalWinnerPick';

const fold = (s) => (s || '').toString().normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '').toLowerCase();

/** The first screen of all: which language is this phone in? Asked once per
 *  phone, then never again — the fan view remembers the answer. */
export const LanguageGate = ({ onPick, current, ty, c, t }) => (
    <div className="rounded-2xl border p-6 text-center"
         style={{ backgroundColor: 'rgba(255,255,255,0.07)', borderColor: `${c.accent}33` }}
         data-testid="goal-lang-gate">
        <p className="text-2xl font-bold mb-1" style={ty.headline}
           data-testid="goal-lang-gate-title">{t('goalGame.guest.langTitle')}</p>
        <p className="text-sm opacity-70 mb-5" data-testid="goal-lang-gate-body">
            {t('goalGame.guest.langBody')}
        </p>
        <div className="space-y-3">
            {[['en', 'English'], ['fr', 'Français'], ['es', 'Español']].map(([code, label]) => (
                <button key={code} type="button" onClick={() => onPick(code)}
                        data-testid={`goal-lang-gate-${code}`}
                        className="w-full min-h-[56px] rounded-xl border px-4 py-3 text-lg font-bold transition-transform active:scale-[0.98]"
                        style={current === code
                            ? { backgroundColor: c.button, color: '#ffffff', borderColor: c.button }
                            : { borderColor: 'rgba(255,255,255,0.25)',
                                backgroundColor: 'rgba(255,255,255,0.06)' }}>
                    {label}
                </button>
            ))}
        </div>
    </div>
);

/** Top left, always visible, on every screen except the main page. */
export const BackBar = ({ onBack, label, title, ty, accent = '' }) => (
    <div className="mb-4" data-testid="goal-back-bar">
        <div className="flex">
            <button type="button" onClick={onBack} data-testid="goal-back-button"
                    aria-label={label}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-white/25 bg-white/10 px-3 py-2 text-sm font-semibold">
                <ArrowLeft size={15} />{label}
            </button>
        </div>
        {/* THE SCREEN'S OWN NAME: its own line under the Back button, centred,
            white, normal heading size, with a thin line in this section's
            colour under it. It never sits beside the button and never clips. */}
        {title && (
            <div className="mt-3">
                <p className="text-center font-bold leading-tight text-white break-words"
                   style={{ fontFamily: ty?.headline?.fontFamily,
                            fontSize: 'clamp(22px, 5vw, 26px)', lineHeight: 1.15 }}
                   data-testid="goal-screen-title">{title}</p>
                <span aria-hidden="true"
                      className="mx-auto mt-2 block h-[3px] w-14 rounded-full"
                      style={{ backgroundColor: accent || 'rgba(255,255,255,0.35)' }} />
            </div>
        )}
    </div>
);

/** THE TWO TEAMS, one against the other. Used by Choose your winner and again
 *  by Choose your star, so the guest learns the screen once. */
export const TeamDuel = ({ teams, onPick, mine, t, c, ty, title, body, homeLabel, awayLabel,
                           heading = null, star = false }) => (
    <div data-testid="goal-team-duel">
        {/* THE STAR SCREEN IS NOT THE WINNER SCREEN. When `star` is set it gets
            its own big heading with the sport image beside the word, and the
            team cards are drawn as outlined plates instead of solid blocks. */}
        {heading}
        <p className={`text-center font-bold ${star ? 'text-lg opacity-80' : 'text-2xl'}`}
           style={star ? undefined : ty.headline}
           data-testid="goal-team-duel-title">{title}</p>
        {body && (
            <p className="mt-1 text-center text-sm opacity-70"
               data-testid="goal-team-duel-body">{body}</p>
        )}
        <div className="mt-5 space-y-3">
            {(teams || []).map((team, i) => (
                <div key={team.side}>
                    <button type="button" onClick={() => onPick(team)}
                            data-testid={`goal-team-${team.side}`}
                            className={`w-full rounded-2xl px-4 py-5 text-left text-white shadow-xl transition-transform active:scale-[0.98] ${
                                star ? 'border-2 border-white/40 bg-white/5 backdrop-blur-sm' : ''} ${
                                mine === team.side ? 'ring-4 ring-white/80' : ''}`}
                            style={star ? { borderColor: `${colourFor(team)}cc` }
                                : { backgroundColor: colourFor(team) }}>
                        <span className="flex items-center gap-3">
                            <span className="rounded-full bg-black/35 px-2.5 py-1 text-[11px] font-black uppercase tracking-wider"
                                  data-testid={`goal-team-${team.side}-side`}>
                                {team.side === 'home' ? homeLabel : awayLabel}
                            </span>
                            <span className="min-w-0 flex-1">
                                <span className="block text-xl font-black leading-tight">
                                    {team.name || team.abbrev}
                                </span>
                                {team.rank && (
                                    <span className="block text-xs font-semibold opacity-90"
                                          data-testid={`goal-team-${team.side}-rank`}>
                                        {team.rank}
                                    </span>
                                )}
                            </span>
                            {mine === team.side && <Check size={20} />}
                        </span>
                    </button>
                    {i === 0 && (
                        <p className="py-1 text-center text-xs font-black uppercase tracking-[0.3em] opacity-50"
                           data-testid="goal-team-duel-vs">{t('goalGame.guest.versus')}</p>
                    )}
                </div>
            ))}
        </div>
    </div>
);

/** "Lock in <this>?" — one choice, one button, nothing else on the screen. */
export const LockConfirm = ({ kicker, name, sub, note, onLock, busy, lockLabel,
                             accentColour, primaryBtn, ty, c, icon = 'trophy' }) => (
    <div className="rounded-2xl border p-6 text-center"
         style={{ backgroundColor: 'rgba(255,255,255,0.07)', borderColor: `${c.accent}33` }}
         data-testid="goal-lock-confirm">
        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full"
             style={{ backgroundColor: accentColour || c.button }}>
            {icon === 'star' ? <Star size={30} color="#fff" /> : <Trophy size={30} color="#fff" />}
        </div>
        <p className="text-sm font-semibold uppercase tracking-wider opacity-70"
           data-testid="goal-lock-kicker">{kicker}</p>
        <p className="mt-1 text-3xl font-black leading-tight" style={ty.headline}
           data-testid="goal-lock-name">{name}</p>
        {sub && <p className="mt-1 text-sm opacity-70" data-testid="goal-lock-sub">{sub}</p>}
        {note && <p className="mt-3 text-xs opacity-60" data-testid="goal-lock-note">{note}</p>}
        <button type="button" onClick={onLock} disabled={busy} style={primaryBtn}
                data-testid="goal-lock-button"
                className="mt-6 w-full min-h-[56px] rounded-xl py-4 text-lg font-bold shadow-lg disabled:opacity-60">
            {busy ? <Loader2 className="mx-auto animate-spin" size={20} /> : lockLabel}
        </button>
    </div>
);

/** One team's players, A to Z, each with his points total. */
export const RosterList = ({ players, onPick, t, c, ty, field, title, searchLabel }) => {
    const [q, setQ] = useState('');
    const rows = useMemo(() => {
        const needle = fold(q.trim());
        return (players || [])
            .filter((p) => !needle || fold(p.name).includes(needle)
                || (p.number || '').startsWith(needle))
            .sort((a, b) => fold(a.name).localeCompare(fold(b.name)));
    }, [players, q]);

    return (
        <div data-testid="goal-roster">
            <p className="text-center text-xl font-bold" style={ty.headline}
               data-testid="goal-roster-title">{title}</p>
            <label className="relative mt-4 block">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchLabel}
                       aria-label={searchLabel} className={`${field} pl-9`}
                       data-testid="goal-roster-search" />
            </label>
            <div className="mt-3 space-y-1.5">
                {rows.length === 0 && (
                    <p className="py-6 text-center text-sm opacity-60" data-testid="goal-roster-empty">
                        {t('goalGame.starNoMatch')}
                    </p>
                )}
                {rows.map((p) => (
                    <button key={p.id} type="button" onClick={() => onPick(p)}
                            data-testid={`goal-roster-row-${p.id}`}
                            className="flex w-full min-h-[56px] items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-left">
                        <span className="w-9 shrink-0 text-center text-sm font-black tabular-nums"
                              style={{ color: c.accent }}>
                            {p.number ? `#${p.number}` : '—'}
                        </span>
                        <span className="min-w-0 flex-1">
                            <span className="block truncate text-base font-semibold">{p.name}</span>
                            {p.position && (
                                <span className="block text-[11px] opacity-60">{p.position}</span>
                            )}
                        </span>
                        {p.points !== null && p.points !== undefined && (
                            <span className="shrink-0 rounded-lg border border-white/20 px-2 py-0.5 text-xs font-bold tabular-nums"
                                  data-testid={`goal-roster-points-${p.id}`}>
                                {t('goalGame.guest.playerPoints', { points: p.points })}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    );
};

/** CLOSE AND COME BACK LATER — what they picked, and why to come back. */
export const ByeScreen = ({ teamName, starName, reminder, t, c, ty, primaryBtn, onBack }) => (
    <div className="rounded-2xl border p-6 text-center"
         style={{ backgroundColor: 'rgba(255,255,255,0.07)', borderColor: `${c.accent}33` }}
         data-testid="goal-bye-screen">
        <p className="text-2xl font-bold" style={ty.headline} data-testid="goal-bye-title">
            {t('goalGame.guest.byeTitle')}
        </p>
        <div className="mt-5 space-y-2 text-left">
            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-black/25 px-4 py-3">
                <Trophy size={16} style={{ color: c.accent }} className="shrink-0" />
                <span className="min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wider opacity-60">
                        {t('goalGame.guest.byeTeam')}
                    </span>
                    <span className="block truncate text-sm font-bold" data-testid="goal-bye-team">
                        {teamName || t('goalGame.guest.byeNothing')}
                    </span>
                </span>
            </div>
            <div className="flex items-center gap-3 rounded-xl border border-white/15 bg-black/25 px-4 py-3">
                <Star size={16} style={{ color: c.accent }} className="shrink-0" />
                <span className="min-w-0">
                    <span className="block text-[10px] font-semibold uppercase tracking-wider opacity-60">
                        {t('goalGame.guest.byeStar')}
                    </span>
                    <span className="block truncate text-sm font-bold" data-testid="goal-bye-star">
                        {starName || t('goalGame.guest.byeNothing')}
                    </span>
                </span>
            </div>
        </div>
        <p className="mt-5 text-base font-semibold" data-testid="goal-bye-reminder">{reminder}</p>
        <button type="button" onClick={onBack} style={primaryBtn} data-testid="goal-bye-back"
                className="mt-6 w-full min-h-[56px] rounded-xl py-4 text-lg font-bold shadow-lg">
            {t('goalGame.guest.byeBack')}
        </button>
    </div>
);

/** One button on the main page. Every one of them opens its own screen —
 *  nothing on the main page expands in place any more.
 *  `tone='hero'` is the big solid call to action (the winner and star picks
 *  until they are made); `done` turns it into the calm confirmed state with a
 *  check, still tappable so a pick can be changed. */
export const MenuButton = ({ icon: Icon, label, hint, onClick, testid, tone, done, c,
                            colour = '', accent = '' }) => {
    const hero = (tone === 'hero' || tone === 'group') && !done;
    // A GROUP BUTTON wears its own soft gradient, 135°, with a thin glossy top
    // highlight and white text — the three big buttons of the main page.
    const grad = tone === 'group' && Array.isArray(colour) ? colour : null;
    const style = done
        ? { backgroundColor: 'rgba(255,255,255,0.07)', borderColor: `${c.accent}66` }
        : grad
            ? { backgroundImage:
                    `linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 42%, rgba(255,255,255,0) 43%),`
                    + ` linear-gradient(135deg, ${grad[0]} 0%, ${grad[1]} 100%)`,
                borderColor: 'rgba(255,255,255,0.14)', color: '#ffffff' }
            : colour
                ? { backgroundColor: colour, borderColor: colour, color: '#ffffff' }
                : hero
                    ? { backgroundColor: c.button, borderColor: c.button, color: '#ffffff' }
                    : tone === 'primary'
                        ? { backgroundColor: c.button, borderColor: c.button, color: '#ffffff' }
                        : { backgroundColor: 'rgba(255,255,255,0.07)',
                            borderColor: 'rgba(255,255,255,0.18)' };
    return (
        <button type="button" onClick={onClick} data-testid={testid}
                className={`flex w-full items-center gap-3 rounded-xl border text-left transition-transform active:scale-[0.99] ${
                    hero ? 'min-h-[72px] px-4 py-4 shadow-xl' : 'min-h-[56px] px-4 py-3'}`}
                style={style}>
            {Icon && <Icon size={hero ? 24 : 18} className="shrink-0"
                          style={(hero || tone === 'primary') && !accent
                              ? undefined : { color: accent || c.accent }} />}
            {/* THE THIN DIVIDER between the icon and the words, as in the design. */}
            {grad && <span aria-hidden="true" className="h-9 w-px shrink-0"
                           style={{ backgroundColor: 'rgba(255,255,255,0.35)' }} />}
            <span className="min-w-0 flex-1">
                <span className={`block font-bold leading-tight ${hero ? 'text-xl' : 'text-base'}`}>
                    {label}
                </span>
                {hint && (
                    <span className={`block ${hero ? 'text-sm opacity-95 leading-snug'
                        : 'truncate text-[11px] opacity-75'}`}>
                        {hint}
                    </span>
                )}
            </span>
            {done && <Check size={20} className="shrink-0" style={{ color: c.accent }} />}
        </button>
    );
};
